import { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { calculateCompoundInterest } from '../../utils/calculations';
import { getTaxConfig, INVESTMENT_TYPES } from '../../utils/taxEngine';
import { TrendingUp, Coins, PiggyBank, Receipt, Percent } from 'lucide-react';

// Animated counter component
const AnimatedValue = ({ value, formatFn, className = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    const duration = 800;
    const startTime = Date.now();
    const startValue = displayValue;
    const endValue = value;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (endValue - startValue) * eased;
      setDisplayValue(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value]);
  
  return (
    <span className={className}>
      {formatFn(Math.round(displayValue))}
    </span>
  );
};

// Result card component
const ResultCard = ({ label, value, icon: Icon, iconColor, gradient, testId, formatFn }) => {
  return (
    <div 
      className={`p-4 rounded-xl border-2 ${gradient} transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs uppercase tracking-[0.12em] font-semibold" style={{ color: 'inherit' }}>
          {label}
        </span>
        <div className={`p-1.5 rounded-lg ${iconColor}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <AnimatedValue 
        value={value} 
        formatFn={formatFn}
        className="text-2xl font-black tracking-tight"
      />
    </div>
  );
};

export const ResultsDisplay = () => {
  const { inputs, t, formatMoney } = useApp();
  
  // Calculate results
  const results = useMemo(() => {
    const taxConfig = getTaxConfig(inputs.investmentType);
    const isUnitLinked = inputs.investmentType === INVESTMENT_TYPES.UNIT_LINKED;
    
    const calcParams = {
      initialDeposit: inputs.initialDeposit,
      monthlyContribution: inputs.monthlyContribution,
      annualInterestRate: inputs.annualInterestRate,
      years: inputs.years,
      compoundingFrequency: inputs.compoundingFrequency,
      contributionFrequency: inputs.contributionFrequency,
      taxRate: inputs.includeTaxes ? taxConfig.interestTaxRate : 0,
      taxOnEachPeriod: taxConfig.taxOnEachPeriod,
      adminFeeRate: isUnitLinked ? inputs.adminFeeRate : 0,
      pauseStartYear: inputs.pauseEnabled ? inputs.pauseStartYear : 0,
      pauseEndYear: inputs.pauseEnabled ? inputs.pauseEndYear : 0,
      useSimpleInterest: taxConfig.useSimpleInterest,
    };
    
    return calculateCompoundInterest(calcParams);
  }, [inputs]);
  
  const isUnitLinked = inputs.investmentType === INVESTMENT_TYPES.UNIT_LINKED;
  
  return (
    <Card 
      className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden"
      data-testid="results-display"
    >
      <CardHeader className="pb-4 border-b border-border/50">
        <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
          <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500/10 to-blue-500/10">
            <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          {t('results')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {/* Main result - Final Value */}
        <div 
          className="mb-6 p-6 rounded-2xl bg-gradient-to-br from-blue-100 to-emerald-100 dark:from-blue-900/40 dark:to-emerald-900/40 border-2 border-blue-200 dark:border-blue-800"
          data-testid="final-value-card"
        >
          <div className="text-xs uppercase tracking-[0.15em] text-blue-700 dark:text-blue-300 font-semibold mb-2">
            {t('finalValue')}
          </div>
          <div className="text-4xl sm:text-5xl font-black tracking-tight text-blue-800 dark:text-blue-200">
            <AnimatedValue value={results.finalValue} formatFn={formatMoney} />
          </div>
        </div>
        
        {/* Secondary results */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ResultCard
            label={t('totalContributions')}
            value={results.totalContributions}
            icon={PiggyBank}
            iconColor="bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300"
            gradient="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200"
            testId="total-contributions"
            formatFn={formatMoney}
          />
          
          <ResultCard
            label={inputs.includeTaxes ? t('interestNet') : t('totalInterest')}
            value={inputs.includeTaxes ? results.totalInterestNet : results.totalInterestGross}
            icon={Coins}
            iconColor="bg-emerald-200 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300"
            gradient="border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200"
            testId="total-interest"
            formatFn={formatMoney}
          />
          
          {inputs.includeTaxes && results.totalTaxPaid > 0 && (
            <ResultCard
              label={t('taxPaid')}
              value={results.totalTaxPaid}
              icon={Receipt}
              iconColor="bg-amber-200 dark:bg-amber-800 text-amber-700 dark:text-amber-300"
              gradient="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
              testId="tax-paid"
              formatFn={formatMoney}
            />
          )}
          
          {isUnitLinked && results.totalAdminFees > 0 && (
            <ResultCard
              label={t('adminFeePaid')}
              value={results.totalAdminFees}
              icon={Percent}
              iconColor="bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300"
              gradient="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200"
              testId="admin-fees-paid"
              formatFn={formatMoney}
            />
          )}
        </div>
        
        {/* Disclaimer */}
        <div className="mt-6 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
          {t('taxDisclaimer')}
        </div>
      </CardContent>
    </Card>
  );
};

export default ResultsDisplay;
